"use client";

import { useState, useEffect } from "react";
import { useDashboard } from "@/contexts/dashboard-context";
import { useNativeLocation } from "@/hooks/use-native-location";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MapPin, Clock } from "lucide-react";

interface EndRouteReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (endTime: string, endLocation: { lat: number; lng: number }) => void;
}

export function EndRouteReminderDialog({
  open,
  onOpenChange,
  onComplete,
}: EndRouteReminderDialogProps) {
  const { activeBlock } = useDashboard();
  const { getCurrentPosition } = useNativeLocation();

  const [endTime, setEndTime] = useState("");
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  // Initialize with block's scheduled end time
  useEffect(() => {
    if (open && activeBlock) {
      setEndTime(activeBlock.shiftEnd);
    }
  }, [open, activeBlock]);

  // Get current GPS on open
  useEffect(() => {
    if (open) {
      setIsLoadingLocation(true);
      getCurrentPosition()
        .then((pos) => {
          if (pos) {
            setCurrentLocation({ lat: pos.lat, lng: pos.lng });
          }
        })
        .finally(() => setIsLoadingLocation(false));
    }
  }, [open, getCurrentPosition]);

  const handleComplete = () => {
    if (currentLocation) {
      onComplete(endTime, currentLocation);
    }
  };

  if (!activeBlock) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Route Complete?</AlertDialogTitle>
          <AlertDialogDescription>
            Your scheduled block ended at {activeBlock.shiftEnd}. Did you finish your route?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* End Time */}
          <div className="space-y-2">
            <Label htmlFor="endTime" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              End Time
            </Label>
            <Input
              id="endTime"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>

          {/* Current Location */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              End Location
            </Label>
            <div className="rounded-lg border p-3 bg-muted/50">
              {isLoadingLocation ? (
                <p className="text-sm text-muted-foreground">Getting location...</p>
              ) : currentLocation ? (
                <p className="text-sm font-mono">
                  {currentLocation.lat.toFixed(5)}, {currentLocation.lng.toFixed(5)}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">Location unavailable</p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Using your current GPS location as the end point
            </p>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Not Yet</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleComplete}
            disabled={!currentLocation || isLoadingLocation}
          >
            Complete Route
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
